import React from 'react';
import {getFirstCapitals} from '../../utils/getFirstCapitals';
import {learnersTableText} from './learnersTableText';
import styles from './LearnersTable.module.css';

export interface LearnersTableRow {
  learnerId: string;
  name: string;
  email: string;
  avatar?: string;
  roleName?: string;
}

interface Props {
  learners: LearnersTableRow[];
  checked: string[];
  page: number;
  pages: number;
  onToggle: (learnerId: string) => void;
  onToggleAll?: () => void;
  onPageChange: (page: number) => void;
  single?: boolean;
  showRole?: boolean;
  footerLabel?: string;
  groupNames?: Record<string, string[]>;
}

const LearnersTable = React.memo(
  ({
    learners,
    checked,
    page,
    pages,
    onToggle,
    onToggleAll,
    onPageChange,
    single,
    showRole,
    footerLabel,
    groupNames,
  }: Props) => {
    const allChecked =
      learners.length > 0 &&
      learners.every(learner => checked.includes(learner.learnerId));
    const someChecked =
      !allChecked &&
      learners.some(learner => checked.includes(learner.learnerId));

    const gridClass = showRole ? styles.withRole : '';
    const boxShape = single ? styles.boxRound : '';

    return (
      <div className={styles.wrapper}>
        <div className={`${styles.head} ${gridClass}`}>
          {single || !onToggleAll ? (
            <span className={styles.headSpacer} />
          ) : (
            <button
              type="button"
              aria-label={learnersTableText.selectAll}
              className={`${styles.box} ${
                allChecked ? styles.boxChecked : ''
              } ${someChecked ? styles.boxPartial : ''}`}
              onClick={onToggleAll}
            />
          )}
          <span className={styles.headName}>{learnersTableText.name}</span>
          <span className={styles.headEmail}>
            {groupNames ? learnersTableText.groups : learnersTableText.email}
          </span>
          {showRole && (
            <span className={styles.headEmail}>{learnersTableText.role}</span>
          )}
        </div>

        <div className={styles.rows}>
          {learners.map(learner => {
            const isChecked = checked.includes(learner.learnerId);
            return (
              <div
                key={learner.learnerId}
                className={`${styles.row} ${gridClass} ${
                  isChecked ? styles.rowChecked : ''
                }`}
                role="button"
                tabIndex={0}
                onClick={() => onToggle(learner.learnerId)}
                onKeyDown={ev => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault();
                    onToggle(learner.learnerId);
                  }
                }}>
                <span
                  className={`${styles.box} ${boxShape} ${
                    isChecked ? styles.boxChecked : ''
                  }`}
                />
                <div className={styles.identity}>
                  {learner.avatar ? (
                    <img
                      src={learner.avatar}
                      alt=""
                      className={styles.avatar}
                    />
                  ) : (
                    <span className={styles.avatar}>
                      {getFirstCapitals(learner.name || '')}
                    </span>
                  )}
                  <span className={styles.name}>{learner.name}</span>
                </div>
                <span className={styles.email}>
                  {groupNames
                    ? (groupNames[learner.learnerId] ?? []).join(', ')
                    : learner.email}
                </span>
                {showRole && (
                  <span className={styles.email}>{learner.roleName}</span>
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.foot}>
          <span className={styles.count}>
            {footerLabel ?? learnersTableText.learnersCount(learners.length)}
          </span>
          <div className={styles.pager}>
            <button
              type="button"
              className={styles.pagerButton}
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}>
              {learnersTableText.previousPage}
            </button>
            <span className={styles.pagerPage}>{page}</span>
            <button
              type="button"
              className={styles.pagerButton}
              disabled={page >= Math.max(pages, 1)}
              onClick={() => onPageChange(page + 1)}>
              {learnersTableText.nextPage}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

export default LearnersTable;
